import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { IsoDate, PublicSettings } from '@brandonstrong/types';
import { addDays, isIsoDate, nightsBetween } from '@brandonstrong/utils';

export interface StayParams {
  checkIn: IsoDate | null;
  checkOut: IsoDate | null;
  adults: number;
  children: number;
}

const clampInt = (value: string | null, fallback: number, min: number, max: number): number => {
  const n = Number(value);
  if (!Number.isInteger(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

/** Stay selection kept in the URL so links are shareable and the back button works. */
export function useStayParams() {
  const [params, setParams] = useSearchParams();

  const stay = useMemo<StayParams>(() => {
    const checkIn = params.get('checkIn');
    const checkOut = params.get('checkOut');
    return {
      checkIn: checkIn && isIsoDate(checkIn) ? checkIn : null,
      checkOut: checkOut && isIsoDate(checkOut) ? checkOut : null,
      adults: clampInt(params.get('adults'), 2, 1, 12),
      children: clampInt(params.get('children'), 0, 0, 12),
    };
  }, [params]);

  const update = useCallback(
    (patch: Partial<StayParams>) => {
      const next = new URLSearchParams(params);
      const merged = { ...stay, ...patch };
      for (const [key, value] of Object.entries(merged)) {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      }
      setParams(next, { replace: true });
    },
    [params, setParams, stay],
  );

  return { stay, update, hasDates: Boolean(stay.checkIn && stay.checkOut) };
}

/** A sensible default stay inside the event window (first two nights). */
export function defaultStay(
  settings: PublicSettings | undefined,
): { checkIn: IsoDate; checkOut: IsoDate } | null {
  if (!settings?.bookableCheckInMin || !settings.bookableCheckOutMax) return null;
  const checkIn =
    settings.eventStartDate && settings.eventStartDate >= settings.bookableCheckInMin
      ? settings.eventStartDate
      : settings.bookableCheckInMin;
  const wanted = addDays(checkIn, Math.max(1, settings.defaultMinNights));
  const checkOut = wanted <= settings.bookableCheckOutMax ? wanted : settings.bookableCheckOutMax;
  return nightsBetween(checkIn, checkOut) >= 1 ? { checkIn, checkOut } : null;
}

/** Query string for links that should carry the current stay along. */
export function stayQuery(stay: StayParams): string {
  const search = new URLSearchParams();
  if (stay.checkIn) search.set('checkIn', stay.checkIn);
  if (stay.checkOut) search.set('checkOut', stay.checkOut);
  search.set('adults', String(stay.adults));
  if (stay.children) search.set('children', String(stay.children));
  return `?${search.toString()}`;
}
